/**
 * QuizQuestionnaireMd Component
 *
 * Displays a modal dialog showing a list of questionnaire answers in read-only mode.
 * Utilizes Chakra UI for modal and layout, and a custom QuestionItemReadOnly component
 * for rendering each questionnaire item.
 *
 * @module components/subQuest/quizzes/QuizQuestionnaireMd
 */

import PropTypes from 'prop-types';

// Chakra UI components
import {
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  VStack,
  Text,
} from '@chakra-ui/react';

// Internal component for displaying a single question in read-only mode
import QuestionItemReadOnly from 'components/question/QuestionItemReadOnly';

/**
 * QuizQuestionnaireMd
 *
 * @param {Object} props - Component props
 * @param {Array<Object>} props.questionnaires - Array of questionnaire objects to display
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {Function} props.onClose - Callback to close the modal
 * @returns {JSX.Element}
 */
function QuizQuestionnaireMd({ questionnaires, isOpen, onClose }) {
  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Answers</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {/* Render each questionnaire item in a vertical stack */}
          <VStack align="start" gap={3}>
            {questionnaires && questionnaires.length > 0 ? (
              questionnaires.map((questionnaire, index) => (
                <QuestionItemReadOnly
                  key={questionnaire.id}
                  index={index}
                  answersJson={questionnaire.answersJson}
                  question={questionnaire.question}
                  mark={questionnaire.mark}
                  remarks={questionnaire.remarks}
                />
              ))
            ) : (
              <Text color="gray.500" textAlign="center" w="100%">
                No answers provided in this session
              </Text>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button colorScheme="gray" variant="outline" mr={3} onClick={onClose}>
            Close
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

// Default props for the component
QuizQuestionnaireMd.defaultProps = {
  questionnaires: [],
  isOpen: false,
  onClose: () => {},
};

// Prop type validation for maintainability and clarity
QuizQuestionnaireMd.propTypes = {
  questionnaires: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      answersJson: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
      question: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
      mark: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      remarks: PropTypes.string,
    })
  ),
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
};

export default QuizQuestionnaireMd;
